/**
 * The Step 6 GraphQL documents: the dashboard, the ops panel, erasure.
 *
 * A third file, by step, for the reason queries.step5.js gives: a reviewer
 * looking at what Step 6 added should not have to diff a two-thousand-line
 * file to find it. `queries.js` re-exports everything.
 */
import { gql } from '@apollo/client';

/**
 * Every field a card needs, in one fragment.
 *
 * The DEFINITION travels with the number. That is the whole point: the ⓘ text
 * and the figure beside it come from one round trip and one source
 * (docs/kpi-catalogue.md via ListMetricDefinitions), so they cannot drift.
 */
export const METRIC_FIELDS = gql`
  fragment MetricFields on Metric {
    name
    buckets
    total
    previousTotal
    deltaPct
    denominator
    suppressed
    rows {
      key
      label
      values
      total
      extraJson
    }
    definition {
      name
      titleFa
      titleEn
      section
      formula
      sources
      grain
      caveat
      needsConfirmation
      isScalar
      isRate
      perUser
    }
  }
`;

export const METRICS_QUERY = gql`
  ${METRIC_FIELDS}
  query DashboardMetrics($names: [String!]!, $filter: MetricFilterInput) {
    metrics(names: $names, filter: $filter) {
      metrics {
        ...MetricFields
      }
      # A failing metric does not fail the dashboard: the card renders its
      # message in place of the number.
      errors {
        name
        message
      }
    }
  }
`;

export const METRIC_QUERY = gql`
  ${METRIC_FIELDS}
  query DashboardMetric($name: String!, $filter: MetricFilterInput) {
    metric(name: $name, filter: $filter) {
      ...MetricFields
    }
  }
`;

export const METRIC_DEFINITIONS_QUERY = gql`
  query MetricDefinitions {
    metricDefinitions {
      name
      titleFa
      titleEn
      section
      formula
      sources
      grain
      caveat
      needsConfirmation
      isScalar
      isRate
      perUser
    }
  }
`;

/** Every number on the dashboard is clickable; this is where it goes. */
export const DRILL_DOWN_QUERY = gql`
  query MetricDrillDown(
    $metric: String!
    $key: String
    $filter: MetricFilterInput
    $limit: Int
  ) {
    metricDrillDown(metric: $metric, key: $key, filter: $filter, limit: $limit) {
      total
      contacts {
        id
        displayName
        collaborationStatus
        # The SCALAR. primaryPhone is a PhoneNumber object, so selecting it
        # bare made this whole query invalid — the drill-down never ran at all.
        primaryPhoneE164
      }
    }
  }
`;

export const ANALYTICS_STATUS_QUERY = gql`
  query AnalyticsStatus {
    analyticsStatus {
      rebuildRunning
      whatsappSessionState
      rowCountsJson
      exchanges {
        exchange
        lastEventAt
        lagSeconds
        appliedCount
        unknownCount
      }
    }
  }
`;

export const SYSTEM_HEALTH_QUERY = gql`
  query SystemHealth {
    systemHealth {
      checkedAt
      services {
        name
        address
        healthy
        detail
        latencyMs
      }
      analytics {
        rebuildRunning
        whatsappSessionState
        rowCountsJson
        exchanges {
          exchange
          lagSeconds
          appliedCount
          unknownCount
        }
      }
      projections {
        recipientCount
        # templateCount, lastEventAt and lagSeconds were selected here and
        # do not exist on ProjectionStatus — which made the whole systemHealth
        # query invalid, so the ops panel never rendered. The lag lives on the
        # per-source rows, which is where the Outbox banner reads it too.
        sources {
          source
          lastEvent
          lastEventAt
          lastAppliedAt
          appliedCount
        }
      }
      whatsapp {
        provider
        connected
        phoneE164
      }
    }
  }
`;

export const AUDIT_LOG_QUERY = gql`
  query AuditLog(
    $page: Int
    $pageSize: Int
    $services: [String!]
    $actorId: ID
    $entityType: String
    $entityId: ID
    $dateFrom: String
    $dateTo: String
  ) {
    auditLog(
      page: $page
      pageSize: $pageSize
      services: $services
      actorId: $actorId
      entityType: $entityType
      entityId: $entityId
      dateFrom: $dateFrom
      dateTo: $dateTo
    ) {
      total
      items {
        id
        service
        actorId
        actorName
        action
        entityType
        entityId
        diffJson
        occurredAt
      }
    }
  }
`;

export const REBUILD_ANALYTICS = gql`
  mutation RebuildAnalytics($exchanges: [String!], $includeDimensions: Boolean) {
    rebuildAnalytics(exchanges: $exchanges, includeDimensions: $includeDimensions) {
      rebuildId
      state
      startedAt
      finishedAt
      replayedJson
      rowsBeforeJson
      rowsAfterJson
      detail
    }
  }
`;

export const ERASE_CONTACT = gql`
  mutation EraseContact($contactId: ID!, $reason: String) {
    eraseContact(contactId: $contactId, reason: $reason) {
      contactId
      clearedFields
      photoDeleted
      downstreamServices
    }
  }
`;
